/**
 * Wildebeast Premium Navigation
 * ---------------------------------------------------------------------------
 * Self-contained custom elements for the premium header. Nothing here touches
 * the stock Horizon header components, so both can coexist in one theme.
 *
 *   <wb-premium-header>    sticky / auto-hide / transparent + mega-menu a11y
 *   <wb-nav-drawer>        mobile drawer, focus trap, accordion sub-nav
 *   <wb-predictive-search> debounced /search/suggest.json + animated keywords
 *
 * Loaded as type="module", so it is deferred by default and never blocks the
 * first paint.
 */

import { StandardEvents } from '@shopify/events';

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Elements that can hold focus — used by the drawer's focus trap. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Locks page scroll without the layout jumping when the scrollbar disappears.
 * Reference-counted, because the drawer and the search overlay can both be
 * open in theory and the second close must not unlock early.
 */
const ScrollLock = {
  count: 0,
  lock() {
    if (this.count === 0) {
      const gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) document.documentElement.style.setProperty('--wb-scrollbar-gap', `${gap}px`);
      document.documentElement.classList.add('wb-scroll-locked');
    }
    this.count += 1;
  },
  unlock() {
    this.count = Math.max(0, this.count - 1);
    if (this.count === 0) {
      document.documentElement.classList.remove('wb-scroll-locked');
      document.documentElement.style.removeProperty('--wb-scrollbar-gap');
    }
  },
};

/** Trailing-edge debounce. */
function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/** Escape user input before it goes anywhere near innerHTML. */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Wraps the matched portion of `text` in <mark>, so the shopper can see why a
 * suggestion matched. Escapes first, then injects the tags — never the other
 * way round.
 */
function highlight(text, query) {
  const safe = escapeHtml(text);
  const term = String(query || '').trim();
  if (!term) return safe;
  const pattern = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return safe.replace(new RegExp(`(${pattern})`, 'ig'), '<mark>$1</mark>');
}

/** Shopify CDN images accept width params; request only what we render. */
function sizedImage(url, width) {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.href;
  } catch {
    return url;
  }
}

/* ===========================================================================
   Header
   =========================================================================== */

class WbPremiumHeader extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('.wb-nav');
    if (!this.root) return;

    this.sticky = this.root.classList.contains('wb-nav--sticky');
    this.autohide = this.root.classList.contains('wb-nav--autohide');
    this.scrollThreshold = Number(this.dataset.scrollThreshold || 24);
    this.lastScroll = window.scrollY;
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();

    this.setupMegaMenus();
    this.setupCartCount();
    this.publishHeight();

    // Keep --wb-header-height accurate through logo shrink and rotation.
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.publishHeight());
      this.resizeObserver.observe(this.root);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  /**
   * Exposes the rendered header height as a CSS variable so sticky filter
   * bars, anchor offsets and the transparent-hero padding all stay in sync
   * with whatever the merchant configures in the editor.
   */
  publishHeight() {
    const height = this.root.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--wb-header-height', `${Math.round(height)}px`);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;

    requestAnimationFrame(() => {
      const y = window.scrollY;

      this.root.classList.toggle('wb-nav--scrolled', y > this.scrollThreshold);

      if (this.autohide) {
        const goingDown = y > this.lastScroll;
        const pastHero = y > this.root.offsetHeight * 2;
        // Never hide while a mega panel or the drawer is open — the shopper
        // would lose the thing they are interacting with.
        const busy = this.root.classList.contains('wb-nav--menu-open');
        this.root.classList.toggle('wb-nav--hidden', goingDown && pastHero && !busy);
      }

      this.lastScroll = y;
      this.ticking = false;
    });
  }

  /**
   * Hover opens the panels via CSS alone (so it works before this script
   * runs). This adds the parts CSS can't do: keyboard support, Escape to
   * close, and aria-expanded bookkeeping.
   */
  setupMegaMenus() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));

    this.triggers.forEach((trigger) => {
      const panel = trigger.parentElement?.querySelector('[data-mega-panel]');
      if (!panel) return;

      trigger.addEventListener('click', (event) => {
        // The trigger is a real link on desktop hover; on keyboard/touch the
        // first activation should open the panel instead of navigating.
        if (window.matchMedia('(hover: hover)').matches && event.detail !== 0) return;
        event.preventDefault();
        const open = panel.dataset.open === 'true';
        this.closeAllPanels();
        if (!open) this.openPanel(trigger, panel);
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          this.openPanel(trigger, panel);
          panel.querySelector(FOCUSABLE)?.focus();
        }
      });

      // Pointer intent: a small close delay stops the panel snapping shut
      // when the cursor crosses the gap between the link and the panel.
      let closeTimer;
      const item = trigger.parentElement;
      item.addEventListener('pointerenter', () => {
        clearTimeout(closeTimer);
        this.root.classList.add('wb-nav--menu-open');
      });
      item.addEventListener('pointerleave', () => {
        closeTimer = setTimeout(() => {
          this.closeAllPanels();
          this.root.classList.remove('wb-nav--menu-open');
        }, 140);
      });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openTrigger = this.triggers.find((t) => t.getAttribute('aria-expanded') === 'true');
      this.closeAllPanels();
      openTrigger?.focus();
    });

    // Close when focus or a click lands outside the header.
    document.addEventListener('focusin', (event) => {
      if (!this.contains(event.target)) this.closeAllPanels();
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeAllPanels();
    });
  }

  openPanel(trigger, panel) {
    trigger.setAttribute('aria-expanded', 'true');
    panel.dataset.open = 'true';
    this.root.classList.add('wb-nav--menu-open');
  }

  closeAllPanels() {
    this.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      panel.dataset.open = 'false';
    });
    this.triggers?.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.root.classList.remove('wb-nav--menu-open');
  }

  /**
   * Keeps the cart bubble truthful. Horizon dispatches cart events, but other
   * apps only touch /cart.js, so we listen broadly and re-fetch.
   */
  setupCartCount() {
    this.cartCounts = Array.from(this.querySelectorAll('[data-cart-count]'));
    if (!this.cartCounts.length) return;

    const refresh = debounce(async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const cart = await response.json();
        this.renderCartCount(cart.item_count);
      } catch {
        /* Offline or blocked — leave the server-rendered count alone. */
      }
    }, 180);

    // StandardEvents.cartLinesUpdate is the one Horizon itself fires; the rest
    // cover apps and older code paths that only know their own event names.
    [StandardEvents.cartLinesUpdate, 'cart:updated', 'cart:refresh', 'variant:add', 'CartDrawer:updated'].forEach((name) => {
      document.addEventListener(name, refresh);
    });
    document.addEventListener('cart:change', refresh);
  }

  renderCartCount(count) {
    this.cartCounts.forEach((node) => {
      const previous = Number(node.textContent.trim()) || 0;
      node.textContent = String(count);
      node.hidden = count === 0;
      if (count !== previous && !REDUCED_MOTION.matches) {
        node.classList.remove('wb-nav__count--bump');
        void node.offsetWidth; // restart the animation
        node.classList.add('wb-nav__count--bump');
      }
    });
  }
}

/* ===========================================================================
   Mobile drawer
   =========================================================================== */

class WbNavDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.scrim = this.querySelector('[data-drawer-scrim]');
    if (!this.panel) return;

    this.openers = Array.from(document.querySelectorAll(`[data-drawer-open="${this.id}"]`));
    this.closers = Array.from(this.querySelectorAll('[data-drawer-close]'));

    this.onKeydown = this.onKeydown.bind(this);

    this.openers.forEach((btn) => btn.addEventListener('click', () => this.open(btn)));
    this.closers.forEach((btn) => btn.addEventListener('click', () => this.close()));
    this.scrim?.addEventListener('click', () => this.close());

    this.setupAccordions();
    this.setupSwipeToClose();
    this.applyStagger();
  }

  /**
   * Row entrance delays are written as inline custom properties instead of
   * nth-child rules, so the stagger keeps working no matter how many links
   * the merchant adds to the menu.
   */
  applyStagger() {
    const step = Number(this.dataset.stagger || 40);
    this.querySelectorAll('.wb-drawer__row').forEach((row, index) => {
      row.style.setProperty('--wb-row-delay', `${60 + index * step}ms`);
    });
  }

  setupAccordions() {
    this.querySelectorAll('[data-drawer-toggle]').forEach((toggle) => {
      const panel = toggle.nextElementSibling;
      if (!panel) return;

      toggle.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';

        // Single-open accordion keeps the drawer short enough to scan.
        if (this.dataset.accordionSingle === 'true' && !isOpen) {
          this.querySelectorAll('[data-drawer-toggle][aria-expanded="true"]').forEach((other) => {
            other.setAttribute('aria-expanded', 'false');
            if (other.nextElementSibling) other.nextElementSibling.dataset.open = 'false';
          });
        }

        toggle.setAttribute('aria-expanded', String(!isOpen));
        panel.dataset.open = String(!isOpen);
      });
    });
  }

  /** Drag the drawer off-screen, the way a native sheet behaves. */
  setupSwipeToClose() {
    let startX = null;
    let currentX = 0;
    const fromRight = this.panel.classList.contains('wb-drawer--right');

    this.panel.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        this.panel.style.transition = 'none';
      },
      { passive: true }
    );

    this.panel.addEventListener(
      'touchmove',
      (event) => {
        if (startX === null) return;
        const delta = event.touches[0].clientX - startX;
        currentX = fromRight ? Math.max(0, delta) : Math.min(0, delta);
        this.panel.style.translate = `${currentX}px 0`;
      },
      { passive: true }
    );

    this.panel.addEventListener('touchend', () => {
      if (startX === null) return;
      this.panel.style.transition = '';
      this.panel.style.translate = '';
      if (Math.abs(currentX) > this.panel.offsetWidth * 0.3) this.close();
      startX = null;
      currentX = 0;
    });
  }

  open(invoker) {
    this.invoker = invoker;
    this.panel.dataset.open = 'true';
    if (this.scrim) this.scrim.dataset.open = 'true';
    this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    ScrollLock.lock();
    document.addEventListener('keydown', this.onKeydown);

    // Move focus in after the panel is actually visible, or Safari drops it.
    requestAnimationFrame(() => {
      this.panel.querySelector(FOCUSABLE)?.focus();
    });
  }

  close() {
    if (this.panel.dataset.open !== 'true') return;
    this.panel.dataset.open = 'false';
    if (this.scrim) this.scrim.dataset.open = 'false';
    this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    ScrollLock.unlock();
    document.removeEventListener('keydown', this.onKeydown);
    this.invoker?.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    // Focus trap: cycle within the drawer while it owns the screen.
    const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

/* ===========================================================================
   Predictive search
   =========================================================================== */

class WbPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.form = this.querySelector('form');
    this.results = this.querySelector('[data-search-results]');
    this.clearBtn = this.querySelector('[data-search-clear]');
    this.reel = this.querySelector('[data-keyword-reel]');

    if (!this.input || !this.results) return;

    this.minChars = Number(this.dataset.minChars || 2);
    this.limit = Number(this.dataset.limit || 6);
    this.types = this.dataset.types || 'product,collection,page,article';
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'INR';
    this.locale = this.dataset.locale || document.documentElement.lang || 'en-IN';
    this.searchRoot = window.Shopify?.routes?.root || '/';

    this.cache = new Map();
    this.activeIndex = -1;
    this.controller = null;

    this.onInput = debounce(() => this.search(this.input.value.trim()), Number(this.dataset.debounce || 220));

    this.input.addEventListener('input', () => {
      this.classList.toggle('wb-search--has-value', this.input.value.length > 0);
      this.onInput();
    });
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= this.minChars) this.openResults();
      else this.showDefaultPanel();
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));

    this.clearBtn?.addEventListener('click', () => {
      this.input.value = '';
      this.classList.remove('wb-search--has-value');
      this.closeResults();
      this.input.focus();
    });

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeResults();
    });

    this.startKeywordRotation();
  }

  disconnectedCallback() {
    clearInterval(this.rotationTimer);
    this.controller?.abort();
  }

  /* ---- Animated keyword placeholder ------------------------------------ */

  /**
   * Scrolls a vertical reel of merchandising keywords behind the cursor.
   * Purely decorative: the reel is aria-hidden and the input keeps a real
   * label, so assistive tech is never chasing moving text.
   */
  startKeywordRotation() {
    if (!this.reel) return;
    const items = this.reel.children;
    if (items.length < 2) return;
    if (REDUCED_MOTION.matches) return;

    const interval = Number(this.dataset.rotateInterval || 2600);
    let index = 0;

    this.rotationTimer = setInterval(() => {
      // Pause while the shopper is actually using the field.
      if (document.activeElement === this.input || this.input.value) return;
      if (document.hidden) return;

      index = (index + 1) % items.length;
      this.reel.style.translate = `0 -${index * 1.4}em`;

      // Seamless wrap: jump back to the top with the transition suppressed.
      if (index === 0) {
        this.reel.style.transition = 'none';
        requestAnimationFrame(() => {
          this.reel.style.transition = '';
        });
      }
    }, interval);
  }

  /* ---- Fetching -------------------------------------------------------- */

  async search(query) {
    if (query.length < this.minChars) {
      this.showDefaultPanel();
      return;
    }

    if (this.cache.has(query)) {
      this.render(this.cache.get(query), query);
      return;
    }

    // Cancel the in-flight request so a slow early keystroke can never
    // overwrite the results for what the shopper has actually typed.
    this.controller?.abort();
    this.controller = new AbortController();

    this.classList.add('wb-search--loading');
    this.renderSkeleton();
    this.openResults();

    const url = new URL(`${this.searchRoot}search/suggest.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', this.types);
    url.searchParams.set('resources[limit]', String(this.limit));
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();
      const resources = data?.resources?.results || {};

      this.cache.set(query, resources);
      this.render(resources, query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderError();
    } finally {
      this.classList.remove('wb-search--loading');
    }
  }

  /* ---- Rendering ------------------------------------------------------- */

  formatMoney(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return escapeHtml(value);
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch {
      return `${this.currency} ${amount}`;
    }
  }

  renderSkeleton() {
    const rows = Array.from({ length: 3 })
      .map(
        () => `
        <div class="wb-search__skeleton">
          <div class="wb-search__skeleton-box" style="aspect-ratio:1"></div>
          <div>
            <div class="wb-search__skeleton-box" style="height:.75rem;width:70%;margin-bottom:.4rem"></div>
            <div class="wb-search__skeleton-box" style="height:.65rem;width:40%"></div>
          </div>
        </div>`
      )
      .join('');
    this.results.innerHTML = `<div class="wb-search__section">${rows}</div>`;
  }

  renderError() {
    this.results.innerHTML = `<p class="wb-search__empty">${escapeHtml(
      this.dataset.errorText || 'Search is unavailable right now. Please try again.'
    )}</p>`;
    this.openResults();
  }

  /** The zero-query panel: popular searches the merchant configured. */
  showDefaultPanel() {
    const raw = this.dataset.popular;
    if (!raw) {
      this.closeResults();
      return;
    }

    const terms = raw.split('|').map((t) => t.trim()).filter(Boolean);
    if (!terms.length) {
      this.closeResults();
      return;
    }

    const chips = terms
      .map(
        (term) =>
          `<li><a class="wb-search__chip" href="${this.searchRoot}search?q=${encodeURIComponent(
            term
          )}&type=product">${escapeHtml(term)}</a></li>`
      )
      .join('');

    this.results.innerHTML = `
      <div class="wb-search__section">
        <p class="wb-search__section-title">${escapeHtml(this.dataset.popularTitle || 'Popular searches')}</p>
        <ul class="wb-search__suggestions">${chips}</ul>
      </div>`;
    this.openResults();
  }

  render(resources, query) {
    const products = resources.products || [];
    const collections = resources.collections || [];
    const pages = resources.pages || [];
    const articles = resources.articles || [];
    const queries = resources.queries || [];

    const total = products.length + collections.length + pages.length + articles.length + queries.length;

    if (total === 0) {
      this.results.innerHTML = `
        <p class="wb-search__empty">
          ${escapeHtml(this.dataset.emptyText || 'No matches for')} “${escapeHtml(query)}”.
        </p>`;
      this.openResults();
      return;
    }

    let html = '';

    if (queries.length) {
      html += `
        <div class="wb-search__section">
          <p class="wb-search__section-title">${escapeHtml(this.dataset.suggestionsTitle || 'Suggestions')}</p>
          <ul class="wb-search__suggestions">
            ${queries
              .map(
                (item) =>
                  `<li><a class="wb-search__chip" href="${escapeHtml(item.url)}" data-search-item>${highlight(
                    item.text,
                    query
                  )}</a></li>`
              )
              .join('')}
          </ul>
        </div>`;
    }

    if (products.length) {
      html += `
        <div class="wb-search__section">
          <p class="wb-search__section-title">${escapeHtml(this.dataset.productsTitle || 'Products')}</p>
          <ul class="wb-search__products">
            ${products.map((product) => this.productRow(product, query)).join('')}
          </ul>
        </div>`;
    }

    const linkGroups = [
      [collections, this.dataset.collectionsTitle || 'Collections'],
      [pages, this.dataset.pagesTitle || 'Pages'],
      [articles, this.dataset.articlesTitle || 'Journal'],
    ];

    linkGroups.forEach(([items, title]) => {
      if (!items.length) return;
      html += `
        <div class="wb-search__section">
          <p class="wb-search__section-title">${escapeHtml(title)}</p>
          <ul class="wb-search__suggestions">
            ${items
              .map(
                (item) =>
                  `<li><a class="wb-search__chip" href="${escapeHtml(item.url)}" data-search-item>${highlight(
                    item.title,
                    query
                  )}</a></li>`
              )
              .join('')}
          </ul>
        </div>`;
    });

    html += `
      <div class="wb-search__footer">
        <a class="wb-search__view-all" href="${this.searchRoot}search?q=${encodeURIComponent(
      query
    )}&type=product" data-search-item>
          ${escapeHtml(this.dataset.viewAllText || 'View all results')} →
        </a>
      </div>`;

    this.results.innerHTML = html;
    this.activeIndex = -1;
    this.openResults();
  }

  productRow(product, query) {
    const image = product.featured_image?.url || product.image;
    const meta = product.product_type || product.vendor || '';
    const price = product.price != null ? this.formatMoney(product.price) : '';

    return `
      <li>
        <a class="wb-search__product" href="${escapeHtml(product.url)}" data-search-item>
          <span class="wb-search__product-media">
            ${
              image
                ? `<img src="${escapeHtml(sizedImage(image, 120))}" alt="${escapeHtml(
                    product.featured_image?.alt || product.title
                  )}" width="60" height="60" loading="lazy" decoding="async">`
                : ''
            }
          </span>
          <span>
            <span class="wb-search__product-title">${highlight(product.title, query)}</span>
            ${meta ? `<span class="wb-search__product-meta">${escapeHtml(meta)}</span>` : ''}
          </span>
          ${price ? `<span class="wb-search__product-price">${price}</span>` : ''}
        </a>
      </li>`;
  }

  /* ---- Keyboard navigation --------------------------------------------- */

  onKeydown(event) {
    const items = Array.from(this.results.querySelectorAll('[data-search-item]'));

    switch (event.key) {
      case 'ArrowDown':
        if (!items.length) return;
        event.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % items.length;
        this.setActive(items);
        break;

      case 'ArrowUp':
        if (!items.length) return;
        event.preventDefault();
        this.activeIndex = this.activeIndex <= 0 ? items.length - 1 : this.activeIndex - 1;
        this.setActive(items);
        break;

      case 'Enter':
        // Only intercept when the shopper has actually highlighted a result;
        // otherwise let the form submit to the full search page.
        if (this.activeIndex >= 0 && items[this.activeIndex]) {
          event.preventDefault();
          items[this.activeIndex].click();
        }
        break;

      case 'Escape':
        this.closeResults();
        this.input.blur();
        break;
    }
  }

  setActive(items) {
    items.forEach((item, index) => {
      const active = index === this.activeIndex;
      item.setAttribute('aria-selected', String(active));
      if (active) item.scrollIntoView({ block: 'nearest' });
    });
  }

  openResults() {
    this.results.dataset.open = 'true';
    this.input.setAttribute('aria-expanded', 'true');
  }

  closeResults() {
    this.results.dataset.open = 'false';
    this.input.setAttribute('aria-expanded', 'false');
    this.activeIndex = -1;
  }
}

/* ===========================================================================
   Search overlay (optional header variant)
   =========================================================================== */

class WbSearchOverlay extends HTMLElement {
  connectedCallback() {
    this.openers = Array.from(document.querySelectorAll(`[data-search-open="${this.id}"]`));
    this.onKeydown = this.onKeydown.bind(this);

    this.openers.forEach((btn) => btn.addEventListener('click', () => this.open(btn)));
    this.querySelectorAll('[data-search-overlay-close]').forEach((btn) =>
      btn.addEventListener('click', () => this.close())
    );

    this.addEventListener('click', (event) => {
      if (event.target === this) this.close();
    });
  }

  open(invoker) {
    this.invoker = invoker;
    this.dataset.open = 'true';
    ScrollLock.lock();
    document.addEventListener('keydown', this.onKeydown);
    requestAnimationFrame(() => this.querySelector('[data-search-input]')?.focus());
  }

  close() {
    if (this.dataset.open !== 'true') return;
    this.dataset.open = 'false';
    ScrollLock.unlock();
    document.removeEventListener('keydown', this.onKeydown);
    this.invoker?.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }
}

/* ===========================================================================
   Cart icon → cart drawer
   ---------------------------------------------------------------------------
   The header cart icons stay real <a href="/cart"> links so they work with
   JS off, on the cart template (where the drawer is deliberately not
   rendered), and for middle-click / open-in-new-tab. When a drawer is on the
   page, an ordinary left click opens it instead of navigating.
   =========================================================================== */

document.addEventListener('click', (event) => {
  // Leave modified clicks alone — they mean "open this URL somewhere else".
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const link = event.target.closest('[data-wb-cart-link]');
  if (!link) return;

  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer) return;

  event.preventDefault();

  // The mobile nav drawer and the cart drawer both lock scroll; closing the
  // nav first keeps the lock reference count balanced.
  const navDrawer = link.closest('wb-nav-drawer');
  if (navDrawer?.dataset.open === 'true') navDrawer.close();

  // A tap landing before theme-drawer.js has upgraded the element still opens
  // the drawer, rather than falling through to a page load.
  customElements.whenDefined('theme-drawer').then(() => drawer.open());
});

/* ===========================================================================
   Registration — guarded so a second load in the theme editor can't throw.
   =========================================================================== */

if (!customElements.get('wb-premium-header')) customElements.define('wb-premium-header', WbPremiumHeader);
if (!customElements.get('wb-nav-drawer')) customElements.define('wb-nav-drawer', WbNavDrawer);
if (!customElements.get('wb-predictive-search')) customElements.define('wb-predictive-search', WbPredictiveSearch);
if (!customElements.get('wb-search-overlay')) customElements.define('wb-search-overlay', WbSearchOverlay);
